// "Mon solde" — dedicated tab on the client dashboard.
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Wallet, QrCode, Banknote, Copy, RefreshCw, Plus, Send, Upload, CheckCircle, ArrowRightLeft, User, Phone } from "lucide-react";
import { useExchangeRate, formatUSD, formatHTG } from "./lib/currency";
import { uploadPaymentProof } from "./lib/uploadProof";

export default function ClientBalanceView({ apiCall, API }) {
  const exchangeRate = useExchangeRate();
  const [balance, setBalance] = useState(0);   // stored in HTG; never displayed in HTG to client
  const [ledger, setLedger] = useState([]);
  // QR codes loaded from backend (single source of truth — admin can update)
  const [qrCodes, setQrCodes] = useState([]);
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeMode, setRechargeMode] = useState("qr"); // "qr" | "affiliate"
  const [amountUsd, setAmountUsd] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState("");
  // === Client → Client transfer modal ===
  const [showTransfer, setShowTransfer] = useState(false);
  const [trPhone, setTrPhone] = useState("");
  const [trAmountUsd, setTrAmountUsd] = useState("");
  const [trNote, setTrNote] = useState("");
  const [trSubmitting, setTrSubmitting] = useState(false);
  // P2P stats — small social-proof badge under the balance hero
  const [p2pStats, setP2pStats] = useState(null);
  // Recent deposits — show the client which recharges are still awaiting admin validation
  const [myDeposits, setMyDeposits] = useState([]);

  const fetchBalance = useCallback(async () => {
    try {
      const r = await apiCall("get", "/balance");
      const nextHtg = Number(r.data?.balance) || 0;
      const rate = Number(r.data?.rate) || exchangeRate || 147;
      const nextUsd = rate ? nextHtg / rate : 0;
      setBalance(prev => {
        const prevUsd = rate ? prev / rate : 0;
        if (nextUsd > prevUsd + 0.001 && prev > 0) {
          const delta = nextUsd - prevUsd;
          toast.success(`💰 +${delta.toFixed(2)} $ crédité — Nouveau solde : ${nextUsd.toFixed(2)} $`, { duration: 6000 });
        } else if (nextUsd < prevUsd - 0.001 && prev > 0) {
          const delta = prevUsd - nextUsd;
          toast(`Débit de ${delta.toFixed(2)} $ — Nouveau solde : ${nextUsd.toFixed(2)} $`);
        }
        return nextHtg;
      });
      setLedger(r.data?.ledger || []);
    } catch (e) { /* noop */ }
  }, [apiCall, exchangeRate]);

  const fetchQrCodes = useCallback(async () => {
    try {
      const r = await apiCall("get", "/payment-qr-codes");
      setQrCodes((r.data?.qr_codes || []).filter(q => q.is_active && q.qr_url));
    } catch { /* noop */ }
  }, [apiCall]);

  const fetchP2pStats = useCallback(async () => {
    try {
      const r = await apiCall("get", "/client/p2p-stats");
      setP2pStats(r.data || null);
    } catch { /* noop */ }
  }, [apiCall]);

  const fetchMyDeposits = useCallback(async () => {
    try {
      const r = await apiCall("get", "/balance/my-deposits?limit=20");
      setMyDeposits(r.data?.deposits || []);
    } catch { /* noop */ }
  }, [apiCall]);

  useEffect(() => { fetchBalance(); fetchQrCodes(); fetchP2pStats(); fetchMyDeposits(); }, [fetchBalance, fetchQrCodes, fetchP2pStats, fetchMyDeposits]);

  // === Auto-refresh balance ===
  // 1) Polling every 15s while the tab is visible
  // 2) Refresh on tab focus / visibility change (instant feedback when client
  //    comes back to the dashboard after admin validates their deposit)
  useEffect(() => {
    let intervalId = null;

    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(() => {
        if (document.visibilityState === "visible") { fetchBalance(); fetchMyDeposits(); }
      }, 15000);
    };
    const stopPolling = () => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    };

    startPolling();

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchBalance();
        fetchMyDeposits();
        startPolling();
      } else {
        stopPolling();
      }
    };
    const onFocus = () => { fetchBalance(); fetchMyDeposits(); };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchBalance, fetchMyDeposits]);

  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = await uploadPaymentProof(file, { role: "client", tokenKey: "client_token" });
      setProofUrl(data.url);
      toast.success("Preuve attachée");
    } catch (err) {
      // err.message contains the real backend detail (or a friendly fallback).
      toast.error(err.message || "Erreur lors du téléversement", { duration: 6000 });
    } finally {
      setUploading(false);
      // Reset the input so the same file can be re-selected after an error.
      try { e.target.value = ""; } catch (_) { /* noop */ }
    }
  };

  const handleSubmitRecharge = async () => {
    const usd = parseFloat(amountUsd);
    if (!usd || usd <= 0) { toast.error("Entrez un montant en USD valide"); return; }
    if (!proofUrl) { toast.error("Joignez la preuve de paiement"); return; }
    const htgAmount = Math.round(usd * exchangeRate * 100) / 100;
    setSubmitting(true);
    try {
      const res = await apiCall("post", "/balance/deposit", {
        amount: htgAmount,
        proof_url: proofUrl,
        note: rechargeMode === "affiliate" ? `Recharge cash via affilié ${affiliateCode || "(non précisé)"}` : "Recharge QR Admin",
      });
      toast.success(res.data.message || "Demande envoyée");
      setShowRecharge(false);
      setAmountUsd(""); setProofUrl(""); setAffiliateCode("");
      fetchBalance();
      fetchMyDeposits();
      toast("⏳ Votre recharge est en attente de validation par l'administrateur (5-10 minutes en moyenne).", { duration: 6000 });
      // Auto-redirect to admin WhatsApp with confirmation message
      try {
        const msg = `Bonjour Admin, j'ai effectué une recharge de ${htgAmount.toFixed(0)} Gdes (≈ ${usd.toFixed(2)} $). Merci de vérifier pour moi s'il vous plaît. Je sais que cela prend seulement 5 à 10 minutes.`;
        const url = "https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?s=cl&p=a&mlu=0&amv=2";
        try { await navigator.clipboard.writeText(msg); } catch {}
        window.open(url, "_blank", "noopener");
      } catch (e) { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const handleSubmitTransfer = async () => {
    const usd = parseFloat(trAmountUsd);
    if (!usd || usd < 0.5) { toast.error("Montant minimum : 0,5 $"); return; }
    const phone = (trPhone || "").trim();
    if (!phone) { toast.error("Entrez le numéro du destinataire"); return; }
    if (!window.confirm(`Confirmer l'envoi de ${usd.toFixed(2)} $ à ${phone} ?`)) return;
    setTrSubmitting(true);
    try {
      const res = await apiCall("post", "/client/balance/transfer", {
        target_phone: phone,
        amount_usd: usd,
        note: trNote.trim() || undefined,
      });
      toast.success(res.data?.message || "Transfert effectué");
      setShowTransfer(false);
      setTrPhone(""); setTrAmountUsd(""); setTrNote("");
      fetchBalance();
      fetchP2pStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du transfert");
    } finally { setTrSubmitting(false); }
  };

  const copyToClipboard = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copié !");
  };

  return (
    <div className="card" data-testid="client-balance-view">
      <h3 className="card-title"><Wallet size={18} /> Mon solde</h3>

      {/* Balance hero — main USD balance */}
      <div style={{ background: "linear-gradient(135deg, #16A34A 0%, #15803D 100%)", padding: "1.2rem 1rem", borderRadius: 14, color: "#FFF", marginBottom: "0.6rem", textAlign: "center" }} data-testid="client-balance-hero">
        <div style={{ fontSize: "0.78rem", opacity: 0.85, fontWeight: 600 }}>SOLDE PRINCIPAL</div>
        <div style={{ fontSize: "2rem", fontWeight: 900, marginTop: 4 }}>{formatUSD(balance, exchangeRate)}</div>
      </div>

      {/* Separate P2P Marketplace balance */}
      <P2PBalanceMiniCard exchangeRate={exchangeRate} apiCall={apiCall} />

      {/* P2P social-proof indicator */}
      {p2pStats && p2pStats.total_transfers > 0 && (
        <div
          data-testid="client-p2p-stats"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "0.6rem 0.8rem",
            background: "linear-gradient(90deg, #F5F3FF 0%, #FAF5FF 100%)",
            border: "1px solid #DDD6FE",
            borderRadius: 10,
            marginBottom: "0.9rem",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, background: "#7C3AED", color: "white", borderRadius: 999, flexShrink: 0 }}>
            <ArrowRightLeft size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "0.7rem", color: "#6D28D9", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Activité P2P
            </div>
            <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }}>
              {p2pStats.total_transfers} transfert{p2pStats.total_transfers > 1 ? "s" : ""}
              {" · "}
              <span style={{ color: "#15803D" }}>↑ {p2pStats.sent_count}</span>
              {" / "}
              <span style={{ color: "#1D4ED8" }}>↓ {p2pStats.received_count}</span>
            </div>
            <div style={{ fontSize: "0.7rem", color: "#475569" }}>
              Volume échangé : <strong>{p2pStats.total_volume_usd.toFixed(2)} $</strong>
              {p2pStats.distinct_partners > 0 && ` · ${p2pStats.distinct_partners} contact${p2pStats.distinct_partners > 1 ? "s" : ""}`}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "0.9rem", flexWrap: "wrap" }}>
        <button className="btn btn-primary btn-sm" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
          onClick={() => setShowRecharge(true)} data-testid="client-balance-recharge-btn">
          <Plus size={16} /> Recharger
        </button>
        <button className="btn btn-sm" style={{ background: "#7C3AED", color: "white", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
          onClick={() => setShowTransfer(true)} data-testid="client-balance-transfer-btn">
          <ArrowRightLeft size={14} /> Transférer
        </button>
        <button className="btn btn-sm" style={{ background: "#E2E8F0", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
          onClick={fetchBalance} data-testid="client-balance-refresh">
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {/* Pending deposits — show the client which recharges are still being validated by the admin */}
      {myDeposits.length > 0 && myDeposits.some(d => d.status === "pending") && (
        <div data-testid="client-pending-deposits" style={{ background: "#FEF3C7", border: "1px solid #F59E0B", borderRadius: 10, padding: "0.7rem 0.85rem", marginBottom: "0.9rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <RefreshCw size={16} style={{ color: "#B45309" }} className="spin" />
            <div style={{ fontWeight: 800, color: "#92400E", fontSize: "0.85rem" }}>
              Recharge en attente de validation
            </div>
          </div>
          <div style={{ fontSize: "0.72rem", color: "#92400E", marginBottom: 8 }}>
            L'administrateur traite votre demande sous 5-10 minutes en moyenne. Le solde sera crédité automatiquement dès validation.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {myDeposits.filter(d => d.status === "pending").slice(0, 5).map((d) => (
              <div key={d.id} data-testid={`client-pending-deposit-${d.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#FFFFFF", borderRadius: 6, padding: "0.4rem 0.55rem", fontSize: "0.78rem" }}>
                <div>
                  <div style={{ fontWeight: 700, color: "#0F172A" }}>{formatUSD(d.amount, exchangeRate)}</div>
                  <div style={{ fontSize: "0.66rem", color: "#64748B" }}>{formatHTG(d.amount)} · {d.created_at ? new Date(d.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                </div>
                <span style={{ background: "#F59E0B", color: "#FFF", padding: "0.15rem 0.5rem", borderRadius: 999, fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.02em" }}>En attente</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transfer modal */}
      {showTransfer && (
        <div className="modal-overlay" data-testid="client-transfer-modal" onClick={(e) => { if (e.target === e.currentTarget) setShowTransfer(false); }}>
          <div className="modal-content" style={{ maxWidth: 460 }}>
            <h3 className="modal-title"><ArrowRightLeft size={18} /> Transférer mon solde</h3>
            <p style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 8 }}>
              Envoyez du solde directement à un autre client par son numéro de téléphone.
            </p>
            <div style={{ background: "#EFF6FF", borderLeft: "3px solid #2563EB", padding: "0.5rem 0.65rem", borderRadius: 6, fontSize: "0.72rem", color: "#1E40AF", marginBottom: 10 }}>
              <strong>Montant minimum : 0,50 $</strong> — Le destinataire reçoit le montant exact que vous indiquez.
            </div>

            <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
              <Phone size={12} /> Numéro du destinataire
            </label>
            <input
              className="form-input"
              type="tel"
              placeholder="Ex: 50955551234"
              value={trPhone}
              onChange={(e) => setTrPhone(e.target.value.replace(/[^0-9+]/g, ""))}
              style={{ marginBottom: 8 }}
              data-testid="transfer-phone"
            />

            <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", display: "block", marginBottom: 4 }}>
              Montant en USD ($)
            </label>
            <input
              className="form-input"
              type="number"
              min="0.5"
              step="0.01"
              placeholder="0.50"
              value={trAmountUsd}
              onChange={(e) => setTrAmountUsd(e.target.value)}
              style={{ marginBottom: 4 }}
              data-testid="transfer-amount-usd"
            />
            {trAmountUsd && parseFloat(trAmountUsd) > 0 && (
              <div style={{ fontSize: "0.7rem", color: "#64748B", marginBottom: 6 }}>
                Solde actuel : {formatUSD(balance, exchangeRate)}
              </div>
            )}

            <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", display: "block", marginBottom: 4, marginTop: 4 }}>
              Note (optionnel)
            </label>
            <input
              className="form-input"
              type="text"
              placeholder="Ex: Cadeau, remboursement…"
              value={trNote}
              onChange={(e) => setTrNote(e.target.value)}
              maxLength={200}
              style={{ marginBottom: 12 }}
              data-testid="transfer-note"
            />

            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-sm" style={{ background: "#E2E8F0", flex: 1 }} onClick={() => setShowTransfer(false)}>Annuler</button>
              <button
                className="btn btn-sm"
                style={{ background: "#7C3AED", color: "white", flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                disabled={trSubmitting || !trAmountUsd || !trPhone || parseFloat(trAmountUsd) < 0.5}
                onClick={handleSubmitTransfer}
                data-testid="transfer-submit"
              >
                <Send size={14} /> {trSubmitting ? "Envoi..." : "Envoyer le transfert"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recharge modal */}
      {showRecharge && (
        <div className="modal-overlay" data-testid="client-recharge-modal" onClick={(e) => { if (e.target === e.currentTarget) setShowRecharge(false); }}>
          <div className="modal-content" style={{ maxWidth: 540 }}>
            <h3 className="modal-title"><Plus size={18} /> Recharger mon solde</h3>

            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <button className="btn btn-sm" style={{ flex: 1, background: rechargeMode === "qr" ? "#2563EB" : "#E2E8F0", color: rechargeMode === "qr" ? "white" : "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
                onClick={() => setRechargeMode("qr")} data-testid="recharge-mode-qr">
                <QrCode size={14} /> Scan QR Admin
              </button>
              <button className="btn btn-sm" style={{ flex: 1, background: rechargeMode === "affiliate" ? "#2563EB" : "#E2E8F0", color: rechargeMode === "affiliate" ? "white" : "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}
                onClick={() => setRechargeMode("affiliate")} data-testid="recharge-mode-affiliate">
                <Banknote size={14} /> Cash via affilié
              </button>
            </div>

            {rechargeMode === "qr" && (
              <div data-testid="recharge-qr-section" style={{ marginBottom: 10 }}>
                <p style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 6 }}>
                  Envoyez le montant à l'un des numéros ci-dessous, puis joignez la preuve.
                </p>
                {qrCodes.length === 0 ? (
                  <div style={{ background: "#FEF3C7", color: "#92400E", padding: "0.6rem 0.8rem", borderRadius: 8, fontSize: "0.78rem", border: "1px solid #FDE68A" }}>
                    Aucun QR code disponible pour l'instant. Veuillez utiliser l'option "Cash via affilié" ou contacter l'administrateur.
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                    {qrCodes.map((qr) => (
                      <div key={qr.id} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 8, textAlign: "center" }}>
                        <img
                          src={qr.qr_url}
                          alt={qr.label}
                          style={{ width: "100%", maxHeight: 110, objectFit: "contain", borderRadius: 6 }}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <div style={{ fontWeight: 700, fontSize: "0.78rem", marginTop: 4 }}>{qr.label}</div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: "0.7rem", color: "#475569" }}>
                          <span>{qr.number}</span>
                          <button onClick={() => copyToClipboard(qr.number)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#2563EB" }} title="Copier"><Copy size={12} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {rechargeMode === "affiliate" && (
              <div data-testid="recharge-affiliate-section" style={{ marginBottom: 10 }}>
                <p style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 6 }}>
                  Donnez votre cash à un affilié de votre quartier. Indiquez son code (optionnel) puis joignez la preuve / reçu signé.
                </p>
                <input className="form-input" placeholder="Code affilié (ex: SMARTECH001)" value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value.toUpperCase())} data-testid="recharge-affiliate-code" />
              </div>
            )}

            <input className="form-input" type="number" min="1" step="0.01" placeholder="Montant en USD"
              value={amountUsd} onChange={(e) => setAmountUsd(e.target.value)} style={{ marginBottom: 6 }} data-testid="recharge-amount-usd" />
            {/* HTG conversion hidden from client per business rule */}

            <div style={{ border: "2px dashed #CBD5E1", borderRadius: 10, padding: "0.75rem", textAlign: "center", cursor: "pointer", background: proofUrl ? "#F0FDF4" : "#F8FAFC", marginBottom: 8 }}
              onClick={() => document.getElementById("client-recharge-proof").click()}>
              <input id="client-recharge-proof" type="file" accept="image/*" style={{ display: "none" }} onChange={handleProofUpload} data-testid="recharge-proof-input" />
              {uploading ? "Téléversement..." : proofUrl ? <span style={{ color: "#16A34A", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><CheckCircle size={16} /> Preuve attachée</span> : <span style={{ color: "#64748B", fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: 4 }}><Upload size={14} /> Joindre la capture</span>}
            </div>

            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-sm" style={{ background: "#E2E8F0", flex: 1 }} onClick={() => setShowRecharge(false)}>Annuler</button>
              <button className="btn btn-primary btn-sm" style={{ flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                disabled={submitting || !amountUsd || !proofUrl} data-testid="recharge-submit"
                onClick={handleSubmitRecharge}>
                <Send size={14} /> {submitting ? "Envoi..." : "Soumettre"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div>
        <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 6, color: "#1E293B" }}>Activité récente</h4>
        {ledger.length === 0 ? (
          <div className="empty-state"><div className="empty-state-text">Aucune transaction</div></div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 360, overflowY: "auto" }}>
            {ledger.slice(0, 30).map((entry, i) => {
              // Determine debit vs credit by kind name (since `amount` is stored as abs value).
              const debitKinds = ["transfer_out", "withdraw", "withdraw_locked", "purchase", "order_payment", "fee", "deposit_fee", "withdraw_fee", "debit"];
              const isDebit = debitKinds.some(k => (entry.kind || "").includes(k));
              const positive = !isDebit;
              return (
                <div key={entry._id || entry.id || `${entry.created_at || ''}-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem 0.6rem", background: positive ? "#F0FDF4" : "#FEF2F2", borderRadius: 8, fontSize: "0.78rem" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "#1E293B" }}>{entry.reason || entry.kind || "Transaction"}</div>
                    <div style={{ color: "#64748B", fontSize: "0.7rem" }}>{(entry.created_at || "").slice(0, 16)}</div>
                  </div>
                  <div style={{ fontWeight: 800, color: positive ? "#15803D" : "#B91C1C" }}>
                    {positive ? "+" : "−"}{formatUSD(entry.amount, exchangeRate)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Mini card showing the dedicated P2P Marketplace balance (separate from main).
function P2PBalanceMiniCard({ exchangeRate, apiCall }) {
  const [balance, setBalance] = useState(0);
  const load = useCallback(async () => {
    try {
      const r = await apiCall("get", "/p2p/balance");
      setBalance(Number(r.data?.balance) || 0);
    } catch { /* silent */ }
  }, [apiCall]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 15000);
    return () => clearInterval(t);
  }, [load]);
  return (
    <div style={{
      background: "linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)",
      padding: "0.7rem 0.9rem", borderRadius: 12, color: "white",
      marginBottom: "0.9rem",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
    }} data-testid="client-p2p-balance-mini">
      <div>
        <div style={{ fontSize: "0.65rem", opacity: 0.85, fontWeight: 700, letterSpacing: 0.5 }}>SOLDE P2P MARKETPLACE</div>
        <div style={{ fontSize: "1.15rem", fontWeight: 900, marginTop: 1 }}>{formatUSD(balance, exchangeRate)}</div>
        <div style={{ fontSize: "0.65rem", opacity: 0.8 }}>Indépendant • réservé Zelle/Wise</div>
      </div>
    </div>
  );
}
